import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  CheckCircle2,
  Loader2,
  FileText,
  Award,
  GraduationCap,
  AlertCircle,
} from 'lucide-react';

export const JobDetailApplyDialog = ({
  open,
  onOpenChange,
  job,
  user,
  coverLetter,
  setCoverLetter,
  applying,
  handleApply,
}) => {
  const [atsConsent, setAtsConsent] = useState(false);
  const isPartnerAts = job.application_method === 'partner_ats';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Apply on I NEED A SOCIAL WORKER</DialogTitle>
          <DialogDescription>
            {isPartnerAts
              ? 'Your application will be securely delivered to the employer\'s applicant tracking system.'
              : `Submit your application to ${job.company_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Document Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Documents to be included:</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Resume</span>
                </div>
                {user?.resume_url ? (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {user.resume_url}
                  </span>
                ) : (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Not uploaded
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">License</span>
                </div>
                {user?.license_url ? (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {user.license_url}
                  </span>
                ) : (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Not uploaded
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Degree</span>
                </div>
                {user?.degree_url ? (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {user.degree_url}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">Optional</span>
                )}
              </div>
            </div>
            {(!user?.resume_url || !user?.license_url) && (
              <p className="text-xs text-amber-600 mt-3">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Missing documents? <a href="/dashboard" className="underline">Update your profile</a> to add them.
              </p>
            )}
          </div>

          {/* Cover Letter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Letter (Optional)
            </label>
            <Textarea
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Tell the employer why you're a great fit for this position..."
              rows={5}
              className="resize-none"
              data-testid="cover-letter-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Write a personalized message for this specific job
            </p>
          </div>

          {/* Partner ATS consent */}
          {isPartnerAts && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="ats-consent-block">
              <label className="flex items-start gap-2 text-sm text-blue-900 cursor-pointer">
                <Checkbox checked={atsConsent} onCheckedChange={(c) => setAtsConsent(!!c)} data-testid="ats-candidate-consent" className="mt-0.5" />
                <span>I authorize I NEED A SOCIAL WORKER to transmit my application, resume, credentials, documents, and application responses to this employer and its applicant tracking system for consideration for this position.</span>
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={applying || (isPartnerAts && !atsConsent)}
            className="bg-primary hover:bg-primary-hover text-white"
            data-testid="submit-application-btn"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
